import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { cn } from '../../lib/utils';

type Props = {
  workItemId: string;
  workItemKey: string;
  projectId: string;
  children: React.ReactNode;
  className?: string;
};

export type OpenWorkItemArgs = {
  workItemId: string;
  workItemKey: string;
  projectId: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
};

export function pageSupportsPeek(pathname: string): boolean {
  return (
    pathname === '/' ||
    pathname === '/my-work' ||
    pathname === '/incidents' ||
    pathname === '/problems' ||
    pathname === '/service-requests' ||
    /^\/releases\/[^/]+$/.test(pathname) ||
    /^\/initiatives\/[^/]+$/.test(pathname) ||
    /\/projects\/[^/]+\/(backlog|tasks|board|archives|completed)$/.test(pathname) ||
    /\/projects\/[^/]+\/work-items\/[^/]+\/board$/.test(pathname) ||
    /\/projects\/[^/]+\/views(\/[^/]+)?$/.test(pathname) ||
    /^\/views\/[^/]+$/.test(pathname)
  );
}

const STREAM_PEEK_RE = /^\/projects\/([^/]+)\//;

/** Peek stays on this page only when the target lives in this stream (or the page is org-wide). */
export function canPeekInPlace(pathname: string, targetProjectId: string): boolean {
  if (!pageSupportsPeek(pathname)) return false;
  const streamId = pathname.match(STREAM_PEEK_RE)?.[1];
  if (!streamId) return true;
  return streamId === targetProjectId;
}

export function useOpenWorkItem() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  return useCallback(
    (args: OpenWorkItemArgs) => {
      if (!args.workItemId || !args.workItemKey || !args.projectId) {
        throw new Error('openWorkItem requires workItemId, workItemKey, and projectId');
      }
      if (args.metaKey || args.ctrlKey) {
        window.open(`/browse/${encodeURIComponent(args.workItemKey)}`, '_blank', 'noopener,noreferrer');
        return;
      }
      if (window.innerWidth < 768) {
        navigate(`/projects/${args.projectId}/work-items/${args.workItemId}`);
        return;
      }
      if (canPeekInPlace(location.pathname, args.projectId)) {
        const next = new URLSearchParams(searchParams);
        next.set('peek', args.workItemKey);
        setSearchParams(next, { replace: false });
        return;
      }
      navigate(`/projects/${args.projectId}/tasks?peek=${encodeURIComponent(args.workItemKey)}`);
    },
    [location.pathname, navigate, searchParams, setSearchParams],
  );
}

export default function ControlLink({
  workItemId,
  workItemKey,
  projectId,
  children,
  className,
}: Props) {
  const openWorkItem = useOpenWorkItem();

  function onClick(event: React.MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    openWorkItem({
      workItemId,
      workItemKey,
      projectId,
      metaKey: event.metaKey,
      ctrlKey: event.ctrlKey,
    });
  }

  return (
    <a
      href={`/browse/${encodeURIComponent(workItemKey)}`}
      onClick={onClick}
      className={cn('hover:text-primary hover:no-underline', className)}
    >
      {children}
    </a>
  );
}
